import { Copy } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  buildDashboardDataTableColumns,
} from '@shigo-ui/dashboard-ui';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shigo-ui/ui';

import { copyToClipboard } from '../../../../../lib/clipboard';
import type { DemandPartner, DemandPartnerAdsTxtRecord } from '../../model';

const columns = buildDashboardDataTableColumns<DemandPartnerAdsTxtRecord>([
  {
    id: 'record',
    kind: DashboardDataTableColumnKind.Text,
    header: 'Record',
    value: toAdsTxtLine,
  },
  {
    id: 'copy',
    kind: DashboardDataTableColumnKind.Actions,
    cell: (record) => (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label="Copy Ads.txt record"
        onClick={() => {
          void copyToClipboard(toAdsTxtLine(record), {
            successMessage: 'Ads.txt record copied.',
          });
        }}
      >
        <Copy className="h-4 w-4" aria-hidden="true" />
      </Button>
    ),
  },
]);

export function DemandPartnerAdsTxtDialog(props: {
  partner: DemandPartner;
  onOpenChange: (open: boolean) => void;
}) {
  const records = props.partner.adsTxtRecords;
  return (
    <Dialog open onOpenChange={props.onOpenChange}>
      <DialogContent className="max-w-5xl p-0">
        <DialogHeader className="border-b px-6 py-5">
          <DialogTitle>Ads.txt records for {props.partner.displayName}</DialogTitle>
          <DialogDescription>View and copy configured Ads.txt records.</DialogDescription>
        </DialogHeader>
        <div className="px-6 py-5">
          <DashboardDataTable<DemandPartnerAdsTxtRecord>
            tableLabel="Ads.txt records"
            columns={columns}
            items={records}
            getRowKey={(record) => record.id ?? toAdsTxtLine(record)}
            emptyMessage="No Ads.txt records are configured."
          />
        </div>
        <DialogFooter className="border-t px-6 py-4">
          <Button
            type="button"
            disabled={!records.length}
            onClick={() => {
              void copyToClipboard(records.map(toAdsTxtLine).join('\n'), {
                successMessage: 'Ads.txt records copied.',
              });
            }}
          >
            <Copy className="h-4 w-4" aria-hidden="true" />
            Copy all records
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              props.onOpenChange(false);
            }}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function toAdsTxtLine(record: DemandPartnerAdsTxtRecord) {
  if (record.fullRecord) return record.fullRecord;
  return [
    record.domain,
    record.publisherAccountId,
    record.accountType,
    record.certAuthority ?? record.certificationAuthorityId,
  ]
    .filter(Boolean)
    .join(', ');
}
